import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MatchData } from '../admin/manageUsersPanel';
import { Alert, Col, Container, Dropdown, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { api } from '../../index';

interface MatchListProps {
    userId?: number;
    setUserId?: Function;
}

const MATCHES_PER_PAGE = 10;

export default function MatchListPage() {
    const [loadingSelf, setLoadingSelf] = useState(true);
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    const getSelfInfo = async () => {
        setLoadingSelf(true);
        setError(null);
        try {
            const res = await api.post('/auth/user', {}, { withCredentials: true });
            setCurrentUser(res.data);
        } catch (e: any) {
            setError(e.response?.data?.message || 'Failed to fetch current user info.');
        } finally {
            setLoadingSelf(false);
        }
    };

    useEffect(() => {
        getSelfInfo();
    }, []);

    return <Container className="my-4 pt-2 pb-2">
        <Row className='mb-4 align-items-center'>
            <Col>
                <h2 className='mb-0'>Match History</h2>
                <p className='text-muted'>previous match data</p>
            </Col>
            <Col xs='auto'>
                <Link to='/home' className='btn btn-outline-secondary btn-sm'>
                    Back to Home
                </Link>
            </Col>
        </Row>
        {error ? <Alert variant="info" className="my-3">Error: {error}</Alert> : null}
        {!loadingSelf ? <MatchList userId={currentUser.user_id} setUserId={undefined} /> : <Spinner animation="border" />}
    </Container>
}

export function MatchList({ userId, setUserId }: MatchListProps) {
    const [matchHistory, setMatchHistory] = useState<MatchData[]>([]);
    const [matchHistoryLoading, setMatchHistoryLoading] = useState(true);
    const [matchHistoryErr, setMatchHistoryErr] = useState<null | string>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedDeckId, setSelectedDeckId] = useState<number | null>(null);

    const [last50count, setlast50count] = useState<number>(Math.max(0, JSON.parse(localStorage.getItem('historyCount') ?? "{}").count ?? 20));

    const getMatchHistory = useCallback(() => {
        setMatchHistoryLoading(true);
        api
            .post('/matches/get', { userId: userId }, { withCredentials: true })
            .then((res: any) => {
                setMatchHistory(res.data);
                setMatchHistoryErr(null);
            })
            .catch(() => {
                window.location.href = '/logout';
            })
            .finally(() => setMatchHistoryLoading(false));
    }, [userId]);

    useEffect(() => {
        getMatchHistory();
    }, [getMatchHistory, userId]);

    const sortedMatches = useMemo(() => {
        return [...matchHistory].sort((a, b) => b.match_id - a.match_id);
    }, [matchHistory]);

    const paginatedMatches = useMemo(() => {
        const start = (currentPage - 1) * MATCHES_PER_PAGE;
        return sortedMatches.slice(start, start + MATCHES_PER_PAGE);
    }, [sortedMatches, currentPage]);

    const totalPages = Math.ceil(sortedMatches.length / MATCHES_PER_PAGE);

    const stats = useMemo(() => {
        if (!userId || matchHistory.length === 0) return null;

        const overallWins = matchHistory.filter(m => m.winner === userId).length;
        const overallRate = (overallWins / matchHistory.length) * 100;

        const last50 = sortedMatches.slice(0, last50count);
        const last50Wins = last50.filter(m => m.winner === userId).length;
        const last50Rate = last50.length > 0 ? (last50Wins / last50.length) * 100 : 0;

        const deckStats: Record<number, { name: string | undefined; wins: number; total: number }> = {};
        matchHistory.forEach(m => {
            const isWinner = m.winner === userId;
            const deckId = isWinner ? m.winner_deck_id : m.loser_deck_id;
            const deckName = isWinner ? m.winner_deck_name : m.loser_deck_name;

            if (!deckStats[deckId]) {
                deckStats[deckId] = { name: deckName, wins: 0, total: 0 };
            }
            deckStats[deckId].total++;
            if (isWinner) deckStats[deckId].wins++;
        });

        return { overallRate, last50Rate, deckStats };
    }, [matchHistory, userId, sortedMatches, last50count]);

    const deckOptions = useMemo(() => {
        if (!stats) return [];
        return Object.entries(stats.deckStats)
            .map(([id, data]) => ({
                id: Number(id),
                name: data.name,
                lastPlayed: sortedMatches.find(m =>
                    m.winner_deck_id === Number(id) || m.loser_deck_id === Number(id)
                )?.match_id ?? 0
            }))
            .sort((a, b) => b.lastPlayed - a.lastPlayed);
    }, [stats, sortedMatches]);

    useEffect(() => {
        if (deckOptions.length > 0 && selectedDeckId === null) {
            setSelectedDeckId(deckOptions[0].id);
        }
    }, [deckOptions, selectedDeckId]);

    const selectedDeckWinRate = useMemo(() => {
        if (!stats || selectedDeckId === null) return null;
        const deck = stats.deckStats[selectedDeckId];
        if (!deck) return null;
        return (deck.wins / deck.total) * 100;
    }, [stats, selectedDeckId]);

    if (matchHistoryLoading)
        return <Spinner animation="border" />;

    if (matchHistoryErr)
        return <Alert variant="info" className="my-3">
            Error: {matchHistoryErr}
        </Alert>;

    if (matchHistory.length < 1)
        return <Alert variant="info" className="my-3">
            No Match History found. Games you play will show here.
        </Alert>;

    return (
        <div className="space-y-4">
            {stats && (
                <Row className="mb-4 g-3">
                    <Col md={4}>
                        <div className="p-3 rounded border bg-light">
                            <div className="text-muted small">Overall Win Rate</div>
                            <div className="h4 mb-0">{stats.overallRate.toFixed(1)}%</div>
                            <div className="text-muted small">
                                {matchHistory.filter(m => m.winner === userId).length} / {matchHistory.length}
                            </div>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div className="p-3 rounded border bg-light">
                            <div className="d-flex align-items-center gap-2">
                                <span className="text-muted small">Last</span>
                                <input
                                    disabled={false}
                                    value={last50count}
                                    onChange={(e) => {
                                        let val = Number(e.target.value);

                                        if (Number.isNaN(val)) {
                                            val = 0;
                                        }

                                        val = Math.max(0, val);

                                        setlast50count(val);
                                        localStorage.setItem("historyCount", JSON.stringify({ count: val }))
                                    }}
                                    size={String(last50count).length || 1}
                                    style={{
                                        width: "auto",
                                        height: "21px",
                                        padding: 0,
                                        marginRight: "-14px",
                                        marginLeft: "-4px",
                                        outline: "none",
                                    }}
                                    className="text-muted small border-0 bg-transparent shadow-none"
                                />
                                <span className="text-muted small">Matches</span>
                            </div>
                            <div className="h4 mb-0">{stats.last50Rate.toFixed(1)}%</div>
                            <div className="text-muted small">
                                {Math.min(sortedMatches.slice(0, last50count).filter(m => m.winner === userId).length, last50count)} / {Math.min(sortedMatches.length, last50count)}
                            </div>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div className="p-3 rounded border bg-light">
                            <Row>
                                <Col>
                                    <div className="text-muted small" style={{ whiteSpace: "nowrap" }}>Win Rate by Deck</div>
                                    {selectedDeckWinRate !== null && (
                                        <>
                                            <div className="h4 mb-0">{selectedDeckWinRate.toFixed(1)}%</div>
                                            <div className="text-muted small">
                                                {stats.deckStats[selectedDeckId!].wins} / {stats.deckStats[selectedDeckId!].total}
                                            </div>
                                        </>
                                    )}
                                </Col>
                                <Col>
                                    <Dropdown>
                                        <Dropdown.Toggle variant="outline-secondary" size="sm" className="w-100">
                                            {selectedDeckId !== null && stats.deckStats[selectedDeckId]
                                                ? stats.deckStats[selectedDeckId].name
                                                : 'Select deck'}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu className="w-100">
                                            {deckOptions.map(opt => (
                                                <Dropdown.Item
                                                    key={opt.id}
                                                    onClick={() => setSelectedDeckId(opt.id)}
                                                    active={opt.id === selectedDeckId}
                                                >
                                                    {opt.name}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                </Row>
            )}

            {paginatedMatches.map((match: MatchData) => {
                const isUserWinner = match.winner === userId;

                const leftPlayer = isUserWinner ? match.winner_username : match.loser_username;
                const leftDeckId = isUserWinner ? match.winner_deck_id : match.loser_deck_id;
                const leftDeckName = isUserWinner ? match.winner_deck_name : match.loser_deck_name;

                const rightPlayer = isUserWinner ? match.loser_username : match.winner_username;
                const rightDeckId = isUserWinner ? match.loser_deck_id : match.winner_deck_id;
                const rightDeckName = isUserWinner ? match.loser_deck_name : match.winner_deck_name;
                const rightPlayerId = isUserWinner ? match.loser : match.winner;

                return (
                    <Row
                        key={match.match_id}
                        className="flex items-center justify-between rounded border border-gray-300 bg-white p-4"
                    >
                        <Col>
                            1v1 random unrated | <span
                                className={`text-lg font-bold ${isUserWinner ? "text-success" : "text-danger"}`}
                            >
                                {isUserWinner ? "Victory" : "Defeat"}
                            </span>
                        </Col>

                        <Col>
                            <span className="font-medium">{leftPlayer}</span>
                            <Link
                                to={`/decks/${leftDeckId}`}
                                style={{ marginLeft: "10px" }}
                            >
                                ({leftDeckName})
                            </Link>
                        </Col>

                        <Col>
                            {setUserId ? <Link to={""} onClick={() => {
                                setUserId(rightPlayerId)
                            }} className="font-medium"> {rightPlayer}</Link> : rightPlayer}
                            <Link
                                to={`/decks/${rightDeckId}`}
                                style={{ marginLeft: "10px" }}
                            >
                                ({rightDeckName})
                            </Link>
                        </Col>
                    </Row>
                );
            })}

            {totalPages > 1 && (
                <Row className="mt-4">
                    <Col className="d-flex justify-content-center gap-2">
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(p => p - 1)}
                        >
                            Previous
                        </button>
                        <span className="btn btn-outline-secondary btn-sm">
                            Page {currentPage} of {totalPages}
                        </span>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(p => p + 1)}
                        >
                            Next
                        </button>
                    </Col>
                </Row>
            )}
        </div>
    );
};